import axios from 'axios'
import { t } from '../../../shared/config/translations'
import {
	parseReleaseDataCsv,
	RELEASE_DATA_MAX_BYTES,
	ReleaseDataError,
} from '../../../shared/releaseDataTransfer'
import {
	downloadReleaseData,
	importReleaseData,
	previewReleaseDataImport,
} from '../../api/releaseData'
import { getSessionSignal } from '../../api/requestSession'
import initDialog from '../../components/Dialog'
import { renderSpinner } from '../../components/Loader'
import { showSnackbar } from '../../components/Snackbar'
import { IS_NATIVE_APP } from '../../config/environment'
import { invalidateCollectionState } from '../../stores/collectionStore'
import { downloadBlob } from '../../utils/download'
import { escapeHtml } from '../../utils/html'

type HistoryImportEntries = string
type HistoryImportPreview = Awaited<ReturnType<typeof previewReleaseDataImport>>

export function bindSettingsHistoryTransfer(rootEl: HTMLElement): void {
	bindHistoryImport(rootEl)
	bindHistoryExport(rootEl)
	bindHistorySample(rootEl)
}

function bindHistoryImport(rootEl: HTMLElement): void {
	const importButtonEl = rootEl.querySelector<HTMLButtonElement>(
		'#settings-history-import-btn',
	)
	const importInputEl = rootEl.querySelector<HTMLInputElement>(
		'#settings-history-import-input',
	)
	if (!importButtonEl || !importInputEl) return

	renderSpinner(importButtonEl)
	importButtonEl.addEventListener('click', () => {
		importInputEl.value = ''
		importInputEl.click()
	})
	importInputEl.addEventListener('change', (event) => {
		void handleHistoryImportChange(event, importInputEl, importButtonEl)
	})
}

function bindHistoryExport(rootEl: HTMLElement): void {
	const exportButtonEl = rootEl.querySelector<HTMLButtonElement>(
		'#settings-history-export-btn',
	)
	if (!exportButtonEl) return

	renderSpinner(exportButtonEl)
	exportButtonEl.addEventListener('click', () => {
		void handleHistoryExport(exportButtonEl)
	})
}

function bindHistorySample(rootEl: HTMLElement): void {
	rootEl
		.querySelector<HTMLButtonElement>('#settings-history-sample-btn')
		?.addEventListener('click', async () => {
			try {
				const response = await fetch(
					'/assets/tunetoss-releases-sample.zip',
				)
				if (!response.ok) throw new Error('Sample download failed')
				const saved = await downloadBlob(
					new Blob([await response.arrayBuffer()], {
						type: 'application/zip',
					}),
					'tunetoss-releases-sample.zip',
				)
				if (saved)
					showSnackbar(
						t(
							IS_NATIVE_APP
								? 'settings.historyFileShared'
								: 'settings.historySampleSuccess',
						),
						'default',
					)
			} catch {
				showSnackbar(t('dialog.errorGeneric'), 'error')
			}
		})
}

async function handleHistoryImportChange(
	event: Event,
	importInputEl: HTMLInputElement,
	importButtonEl: HTMLButtonElement,
): Promise<void> {
	event.stopPropagation()
	const file = importInputEl.files?.[0]
	if (!file) return
	await handleHistoryImport(file, importButtonEl)
}

async function handleHistoryImport(
	file: File,
	importButtonEl: HTMLButtonElement,
): Promise<void> {
	const sessionSignal = getSessionSignal()
	setButtonLoading(importButtonEl, true)

	try {
		if (file.size > RELEASE_DATA_MAX_BYTES) {
			throw new ReleaseDataError(t('settings.historyImportFileTooLarge'))
		}
		const entries = await file.text()
		parseReleaseDataCsv(entries)
		const summary = await previewReleaseDataImport(entries)
		if (sessionSignal.aborted) return
		showHistoryImportPreviewDialog(file, entries, summary)
	} catch (error) {
		if (sessionSignal.aborted) return
		showHistoryImportError(error)
	} finally {
		setButtonLoading(importButtonEl, false)
	}
}

function showHistoryImportPreviewDialog(
	file: File,
	entries: HistoryImportEntries,
	summary: HistoryImportPreview,
): void {
	const previewItems = [
		[t('settings.historyImportRows'), summary.totalRows],
		[t('settings.historyImportReleases'), summary.releaseCount],
		[t('settings.historyImportDated'), summary.datedCount],
		[t('settings.historyImportUnknown'), summary.unknownCount],
		[t('settings.historyImportDuplicates'), summary.duplicateDatedCount],
		[t('settings.historyImportNew'), summary.importedDatedCount],
		[t('settings.releaseImportFavorites'), summary.favoriteCount],
		[t('settings.releaseImportHidden'), summary.hiddenCount],
		[
			t('settings.releaseImportSkippedFavorites'),
			summary.skippedFavoriteCount,
		],
	]

	initDialog({
		id: 'history-import-preview-dialog',
		cancelText: t('dialog.cancel'),
		onErrorMessage: t('settings.releaseImportInterrupted'),
		confirmText: t('settings.historyImportConfirm'),
		content: `
			<p>${t('settings.historyImportPreviewIntro').replace('%{filename}', escapeHtml(file.name))}</p>
			<p>${t('settings.releaseImportMergeNotice')}</p>
			<p>${t('settings.releaseImportFavoriteNotice')}</p>
			<dl class="history-import-preview">
				${previewItems.map(([label, value]) => `<div><dt>${label}</dt><dd>${value}</dd></div>`).join('')}
			</dl>
		`,
		onConfirm: async () => {
			const sessionSignal = getSessionSignal()
			const result = await importReleaseData(entries).finally(() => {
				if (!sessionSignal.aborted) invalidateCollectionState()
			})
			if (sessionSignal.aborted) return
			showSnackbar(
				t('settings.historyImportSuccess')
					.replace('%{releases}', String(result.releaseCount))
					.replace('%{dated}', String(result.importedDatedCount))
					.replace('%{unknown}', String(result.importedUnknownCount)),
				'default',
			)
		},
		snackbarType: null,
		title: t('settings.historyImportPreviewTitle'),
	})
}

async function handleHistoryExport(
	exportButtonEl: HTMLButtonElement,
): Promise<void> {
	const sessionSignal = getSessionSignal()
	setButtonLoading(exportButtonEl, true)

	try {
		const saved = await downloadReleaseData()
		if (!saved || sessionSignal.aborted) return
		showSnackbar(
			t(
				IS_NATIVE_APP
					? 'settings.historyFileShared'
					: 'settings.historyExportSuccess',
			),
			'default',
		)
	} catch (error) {
		if (sessionSignal.aborted) return
		console.error('History export failed', error)
		showSnackbar(t('dialog.errorGeneric'), 'error')
	} finally {
		setButtonLoading(exportButtonEl, false)
	}
}

function setButtonLoading(
	buttonEl: HTMLButtonElement,
	isLoading: boolean,
): void {
	const spinnerEl = buttonEl.querySelector<HTMLDivElement>('.spinner')
	buttonEl.disabled = isLoading
	spinnerEl?.classList.toggle('visible', isLoading)
}

function showHistoryImportError(error: unknown): void {
	if (
		axios.isAxiosError(error) &&
		typeof error.response?.data?.error === 'string'
	) {
		error = new ReleaseDataError(
			error.response.data.error,
			error.response.data.row,
		)
	}

	if (error instanceof ReleaseDataError) {
		const message = error.row
			? t('settings.historyImportValidationError')
					.replace('%{row}', String(error.row))
					.replace('%{message}', error.message)
			: error.message
		showSnackbar(message, 'error')
		return
	}
	console.error('History import failed', error)
	showSnackbar(t('dialog.errorGeneric'), 'error')
}
